import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { z } from 'zod'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { readResponseJson, getResponseDebugContext } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** Load the complete membership snapshot only when opening the document picker. */
export async function getTableDocumentIds(tableId: string, signal?: AbortSignal): Promise<string[]> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('Sign in to access tables.')
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/tables/${tableId}/document-ids`
  const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal })
  const { data, parseError } = await readResponseJson(response)
  signal?.throwIfAborted()
  if (!response.ok) {
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined
    if (response.status >= 500) Sentry.captureException(new Error('Table document request failed'), { extra: getResponseDebugContext(response, url) })
    throw new ApiError(
      response.status,
      response.status >= 400 && response.status < 500 && typeof detail === 'string' ? detail : 'Unable to load existing table documents.',
      response.status < 500 ? detail : undefined
    )
  }
  const parsed = z.array(z.string().uuid()).safeParse(data)
  if (parseError || !parsed.success) {
    Sentry.captureException(new Error('Invalid table document response'), { extra: getResponseDebugContext(response, url) })
    throw new ApiResponsePayloadError(response.status, 'The table document list could not be read. Please retry.')
  }
  return parsed.data
}
